import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { getOmjsViewInstance, getOperationData } from 'selectors';
import { IState } from 'services/root-reducer';
import { actions as mapActions } from 'services/map/map-service';
import { actions as operationActions } from 'services/operations/operations-service';
import {
    Button, ButtonGroup, Icons, AutoScrollbars,
    PanelHeader, PanelBody, PanelFooter, CloseButton
} from 'components';
import { IGeometry } from 'types/omjs/project';
import i18next from 'i18next';

import './styles.scss';

interface IPkkBalloonData {
    features: GeoJSON.Feature[]
}

interface IPkkObjectBalloonComponentProps {
    data: IPkkBalloonData,
    pkkSettings: { [key: string]: any },
    view: IState['omjs']['omjsViewInstance'];
}

interface IPkkObjectBalloonComponentState {
    active: number
}

const mapStateToProps = (state: IState) => ({
    data: getOperationData(state, 'SHOW_PKK_BALLOON'),
    pkkSettings: state.app.config.publication.settings.customWidgets.pkk_settings?.values,
    view: getOmjsViewInstance(state)
});

class PkkObjectBalloonComponent extends Component<IPkkObjectBalloonComponentProps, IPkkObjectBalloonComponentState> {
    constructor(props) {
        super(props);
        this.state = { active: 0 };
        this.zoomToObject = this.zoomToObject.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        const { data, pkkSettings } = this.props;
        const { active } = this.state;
        const activeFeature = data?.features?.[active];

        if (JSON.stringify(prevProps.data?.features) !== JSON.stringify(data?.features)) {
            if (pkkSettings.pkk_highlight_object) {
                mapActions.highlightCadastreGeometry(activeFeature?.geometry as IGeometry || null);
            }
        }

        if (prevState.active !== active) {
            if (pkkSettings.pkk_highlight_object) {
                mapActions.highlightCadastreGeometry(activeFeature?.geometry as IGeometry || null);
            }
        }
    }

    componentWillUnmount() {
        mapActions.highlightCadastreGeometry(null);
        operationActions.clearOperation('SHOW_PKK_BALLOON');
    }

    setActive(forward: boolean = false) {
        const { data } = this.props;
        const { active } = this.state;

        let newActive = active + (forward ? 1 : -1);
        if (newActive < 0) {
            newActive = data.features.length - 1;
        } else if (newActive === data.features.length) {
            newActive = 0;
        }

        this.setState({ active: newActive });
    }

    zoomToObject() {
        const { data } = this.props;
        const { active } = this.state;
        const activeFeature = data.features[active];
        const geom = new omjs.ol.format.GeoJSON().readGeometry(activeFeature.geometry);
        mapActions.fitToExtent(geom.getExtent());
    }

    renderHeader() {
        return (
            <PanelHeader className="balloon">
                <div className="container-title">
                    <span className="title">
                        {i18next.t('Информация по объекту')}
                    </span>
                </div>
                <ButtonGroup>
                    <CloseButton
                        title={i18next.t('Закрыть')}
                        onClick={() => {
                            mapActions.hideBalloon();
                        }}
                    />
                </ButtonGroup>
            </PanelHeader>
        );
    }

    renderFeaturesList() {
        const { data } = this.props;
        const { active } = this.state;
        const activeFeature = data.features[active];
        return (
            <div className="balloon-features">
                <Button
                    className="btn-left"
                    variant="black"
                    icon={<Icons.IconArrowLeft />}
                    onClick={() => { this.setActive(false); }}
                    rounded
                />
                <div className="container-title">
                    <span className="text value">
                        {activeFeature?.properties?.cadastral_number || ''}
                    </span>
                </div>
                <Button
                    className="btn-right"
                    variant="black"
                    icon={<Icons.IconArrowRight />}
                    onClick={() => { this.setActive(true); }}
                    rounded
                />
            </div>
        );
    }

    renderRow(fieldName, value) {
        return (
            <div key={fieldName} className="row">
                <span className="text name">{fieldName}</span>
                <span className="text value">{value}</span>
            </div>
        );
    }

    renderObjectInfo(activeFeature: GeoJSON.Feature) {
        const { cadastral_number, name, address } = activeFeature.properties;

        let fields = [];
        if (cadastral_number) fields.push(this.renderRow(`${i18next.t('Кадастровый номер')}`, cadastral_number));
        if (name) fields.push(this.renderRow(`${i18next.t('Наименование')}`, name));
        if (address) fields.push(this.renderRow(`${i18next.t('Адрес')}`, address));

        return (
            <div className='rows'>
                {fields}
            </div>
        );
    }

    renderActiveObject() {
        const { data } = this.props;
        const { active } = this.state;
        const activeFeature = data.features[active];
        return (
            <div className="wrapper-content-ballon-pkk">
                <AutoScrollbars style={{ height: 'auto' }}>
                    {activeFeature
                        ? this.renderObjectInfo(activeFeature)
                        : this.renderRow(i18next.t('Нет данных'), null)}
                </AutoScrollbars>
            </div>
        );
    }

    renderBody() {
        const { data } = this.props;
        return (
            <PanelBody className="balloon">
                {(data.features.length > 1) && this.renderFeaturesList()}
                {this.renderActiveObject()}
            </PanelBody>
        );
    }

    renderFooter() {
        return (
            <PanelFooter className="balloon">
                <ButtonGroup orientation="horizontal">
                    <Button
                        icon={<Icons.IconMagnifyGlassPlus />}
                        onClick={this.zoomToObject}
                        title={i18next.t('Приблизить')}
                    />
                </ButtonGroup>
            </PanelFooter>
        );
    }

    render() {
        const { data } = this.props;
        if (!data) return null;
        return (
            <Fragment>
                {this.renderHeader()}
                {this.renderBody()}
                {this.renderFooter()}
            </Fragment>
        );
    }
}

export const PkkObjectBalloon = connect(mapStateToProps)(PkkObjectBalloonComponent);
